/*
 * Collection scroll restore — the "back from a PDP" jump.
 *
 * Collection grids here are rendered client-side by Algolia, so on a back
 * navigation the document is briefly just header + skeleton. The browser tries
 * to put the old scroll offset back against that short page, clamps to the top,
 * and then the page jumps down once the grid fills in. That flash of the top of
 * the collection is what this removes.
 *
 * Two halves, because one of them has to beat first paint:
 *
 *   1. A small inline bootstrap in layout/theme.liquid, immediately after the
 *      Algolia skeleton. It reserves enough page height, pads the skeleton out
 *      to the number of cards the shopper had, and scrolls — all before the
 *      browser paints. It has to be inline: a deferred script runs after paint,
 *      which is the one thing this feature cannot afford. It hands its state to
 *      this file on window.__dpdCollScroll.
 *
 *   2. This file (deferred). Records the position while the shopper browses,
 *      and releases the height reservation once the real grid is in flow.
 *
 * Keyed on pathname + search, which here is the whole result set: Algolia syncs
 * both the page number (?page=2) and the filters into the URL, so each distinct
 * grid gets its own entry and a position is never restored onto a different set
 * of results.
 *
 * The reservation goes on #MainContent, not on the skeleton. At swap time the
 * skeleton is taken out of flow (position:absolute) and the real container is
 * put into it, so anything reserved on the skeleton would vanish in the same
 * frame the real grid appears — reintroducing the jump. #MainContent wraps both
 * and stays in flow throughout, so the page height is continuous across the
 * swap.
 */
(function () {
  'use strict';

  var PREFIX = 'dpd:collscroll:';
  // Near the top already: the browser handles that fine on its own. Kept in
  // step with the same floor in the inline bootstrap, which also enforces the
  // 30-minute freshness cut-off and the card-count cap on the read side.
  var MIN_OFFSET = 200;
  // If the skeleton never swaps (Algolia down, script error), let the
  // reservation go anyway rather than leaving the page padded out forever.
  var SWAP_TIMEOUT_MS = 8000;
  // How long scroll events coalesce before a write.
  var SAVE_THROTTLE_MS = 150;

  var GRID = '.ais-hits-container';
  var CARD = '.ais-hits-container .ais-product';

  function storageKey() {
    return PREFIX + location.pathname + location.search;
  }

  function onCollectionGrid() {
    return !!document.querySelector('#algolia-shopify-instantsearch, #algolia-placeholder');
  }

  /* ---- record ----------------------------------------------------------- */

  function save() {
    if (!onCollectionGrid()) return;
    try {
      var y = Math.round(window.scrollY);
      if (y < MIN_OFFSET) {
        // Clear rather than store: a shopper who scrolled back to the top
        // should not be sent down again on their next visit.
        sessionStorage.removeItem(storageKey());
        return;
      }
      var grid = document.querySelector(GRID);
      sessionStorage.setItem(storageKey(), JSON.stringify({
        y: y,
        cards: document.querySelectorAll(CARD).length,
        gridH: grid ? Math.round(grid.getBoundingClientRect().height) : 0,
        ts: Date.now()
      }));
    } catch (e) {
      // Private mode, quota, disabled storage. Restoring is a nicety, so a
      // failure here must never surface.
    }
  }

  // Trailing timeout rather than requestAnimationFrame: rAF is paused while the
  // tab is hidden, and a shopper who scrolls, switches tab, then comes back and
  // clicks a product would otherwise have nothing recorded. Saving a scroll
  // offset does not need frame alignment anyway.
  var saveTimer = null;
  function saveThrottled() {
    if (saveTimer) return;
    saveTimer = setTimeout(function () {
      saveTimer = null;
      save();
    }, SAVE_THROTTLE_MS);
  }

  window.addEventListener('scroll', saveThrottled, { passive: true });
  // pagehide covers an ordinary link click, a bfcache suspend and a tab close
  // in one hook, and unlike beforeunload it does not disqualify the page from
  // the back/forward cache.
  window.addEventListener('pagehide', save);
  // Belt and braces for the exact case this exists for — the click that leaves
  // for a product page. Capture phase, so it still runs if a card's own
  // handler stops propagation.
  document.addEventListener('click', function (e) {
    if (e.target && e.target.closest && e.target.closest(GRID + ' a')) save();
  }, true);

  /* ---- release the reservation ------------------------------------------ */

  var state = window.__dpdCollScroll;
  if (!state || !state.y || !state.el) return;

  var released = false;

  function release() {
    if (released) return;
    released = true;

    // Re-assert before dropping the reservation: the real grid may not be
    // pixel-identical to the skeleton, so this lands us on the saved offset
    // while the page is still guaranteed tall enough to hold it.
    window.scrollTo({ top: state.y, behavior: 'instant' });
    state.el.style.removeProperty('min-height');

    // If the real grid came back shorter than it was — a product sold out, the
    // result count changed — the browser clamps. Landing as close as the page
    // now allows still beats landing at the top.
    var max = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
    if (Math.round(window.scrollY) !== state.y) {
      window.scrollTo({ top: Math.min(state.y, max), behavior: 'instant' });
    }

    // Put back exactly what the bootstrap found. The theme sets 'manual'
    // globally, so hardcoding 'auto' here would quietly change behaviour for
    // every later navigation on this history entry.
    try { history.scrollRestoration = state.prevRestoration || 'auto'; } catch (e) {}
    window.__dpdCollScroll = null;
  }

  function swapped() {
    return document.body.classList.contains('algolia-custom-loaded');
  }

  // rAF gives the right timing (after the swap's layout), but it is paused in a
  // hidden tab, so pair it with a timeout backstop.
  function scheduleRelease() {
    requestAnimationFrame(release);
    setTimeout(release, 300);
  }

  if (swapped()) {
    scheduleRelease();
  } else {
    // algolia-hooks.js signals the skeleton→real swap by putting
    // .algolia-custom-loaded on <body> (hidePlaceholder), so that class is the
    // signal that the real grid is carrying the page height.
    var observer = new MutationObserver(function () {
      if (!swapped()) return;
      observer.disconnect();
      scheduleRelease();
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    setTimeout(function () {
      observer.disconnect();
      release();
    }, SWAP_TIMEOUT_MS);
  }
})();
